//
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Button, Row, Col } from "react-bootstrap";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./tour-slider.scss";

import { ReactComponent as ChevronRightIcon } from "../../assets/images/btn-right-aero.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  benefit_info,
  career_info,
  feature_info,
} from "../../redux/slices/careerinfoSlice";

// Optimized image component with loading state and fade-in effect
const OptimizedImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`optimized-image-wrapper ${loaded ? "loaded" : "loading"}`}>
      {!loaded && !error && (
        <div className="image-placeholder" aria-hidden="true">
          <div className="placeholder-shimmer" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`${className} ${loaded ? "fade-in" : "hidden"}`}
        loading="eager"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
};

// Preload images for upcoming slides
const useImagePreloader = (imageUrls) => {
  useEffect(() => {
    if (!imageUrls || imageUrls.length === 0) return;

    const preloadImages = imageUrls.map((url) => {
      const img = new Image();
      img.src = url;
      return img;
    });

    return () => {
      preloadImages.forEach((img) => {
        img.src = "";
      });
    };
  }, [imageUrls]);
};

export default function TourSlider({ show, onSkip, onComplete }) {
  const sliderRef = useRef(null);
  const dispatch = useDispatch();

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const store = useSelector((state) => state.career);

  useEffect(() => {
    dispatch(career_info());
    dispatch(feature_info());
    dispatch(benefit_info());
  }, [dispatch]);

  // Collect all image URLs for preloading
  const allImageUrls = useMemo(() => {
    const urls = [];
    const career = store?.career_infoItems?.data?.career_info;
    const feature = store?.feature_infoItems?.data?.feature_info;
    const benefit = store?.benefit_infoItems?.data?.benefit_info;

    [career, feature, benefit].forEach((data) => {
      (data?.sub_content || []).forEach((item) => {
        if (item.icon_path) {
          urls.push(`${process.env.REACT_APP_API_BASE_URL}/${item.icon_path}`);
        }
      });
    });

    return urls;
  }, [
    store?.career_infoItems,
    store?.feature_infoItems,
    store?.benefit_infoItems,
  ]);

  // Preload all images when data is available
  useImagePreloader(allImageUrls);

  const slides = useMemo(() => {
    const career = store?.career_infoItems?.data?.career_info;
    const feature = store?.feature_infoItems?.data?.feature_info;
    const benefit = store?.benefit_infoItems?.data?.benefit_info;

    return [
      {
        title: career?.main_title || "Make informed career decisions",
        subtitle:
          career?.main_description ||
          "Compare nursing salaries across different locations, adjusted for cost of living",
        footer:
          career?.main_footer ||
          "Data sourced from U.S. Bureau of Labor Statistics (BLS.gov)",
        items: (career?.sub_content || []).map((item) => ({
          description: item.overview_info,
          icon: (
            <OptimizedImage
              src={`${process.env.REACT_APP_API_BASE_URL}/${item.icon_path}`}
              alt=""
              className=""
            />
          ),
        })),
      },
      {
        title: feature?.main_title || "Powerful tools",
        subtitle:
          feature?.main_description ||
          "Compare nursing salaries across different locations",
        footer:
          career?.main_footer ||
          "Data sourced from U.S. Bureau of Labor Statistics (BLS.gov)",
        items: (feature?.sub_content || []).map((item) => ({
          text: item.sub_title,
          description: item.sub_description,
          icon: (
            <OptimizedImage
              src={`${process.env.REACT_APP_API_BASE_URL}/${item.icon_path}`}
              alt=""
              className="feature-icon"
            />
          ),
        })),
      },
      {
        title: benefit?.main_title || "Powerful tools",
        subtitle:
          benefit?.main_description ||
          "Compare nursing salaries across different locations",
        footer:
          career?.main_footer ||
          "Data sourced from U.S. Bureau of Labor Statistics (BLS.gov)",
        items: (benefit?.sub_content || []).map((item) => ({
          text: item.sub_title,
          description: item.sub_description,
          icon: (
            <OptimizedImage
              src={`${process.env.REACT_APP_API_BASE_URL}/${item.icon_path}`}
              alt=""
              className="feature-icon last-slide-img"
            />
          ),
        })),
      },
    ];
  }, [
    store?.career_infoItems,
    store?.feature_infoItems,
    store?.benefit_infoItems,
  ]);

  const settings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: false,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    beforeChange: (_, next) => setCurrent(next),
  };

  const handleNext = () => {
    if (!sliderRef.current) return;
    const slider = sliderRef.current;
    const idx = slider.innerSlider?.state?.currentSlide ?? current;
    if (idx < slides.length - 1) {
      slider.slickNext();
    } else {
      onComplete?.();
    }
  };

  // When on the last slide, auto-complete after 5s of inactivity
  // useEffect(() => {
  //   if (!show) return;
  //   if (current !== slides.length - 1) return;
  //   const id = setTimeout(() => onComplete?.(), 5000);
  //   return () => clearTimeout(id);
  // }, [current, show]);

  return (
    // <Modal show={show} onHide={onSkip} size="lg" dialogClassName="tour-modal">
    <div className="form-content-card w-100">
      <div
        // p-3
        className="content-wrap tour-slider"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="step-form">
          <div className="step-title-subtitle">
            <div className="title-subtitle w-100">
              <h5>{slides[current].title}</h5>
              <p className="step-subtitle">{slides[current].subtitle}</p>
            </div>
          </div>

          <Slider ref={sliderRef} {...settings}>
            {slides.map((slide, i) => (
              <div key={i}>
                <div className="feature-pill-content">
                  <Row>
                    {slide.items.map((item, idx) => (
                      <Col md={6} lg={4} key={idx} className="feature-pill">
                        <div className="contentwrap">
                          <div className="icon-box" aria-hidden>
                            {item.icon}
                          </div>
                          <div className="content">
                            <div className="feature-title">{item.text}</div>
                            {item.description && (
                              <div className="feature-description">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            ))}
          </Slider>
          {/* <div className="text-center slider-content">
            <p>Data sourced from U.S. Bureau of Labor Statistics (BLS.gov)</p>
          </div> */}
          {slides[current]?.footer && (
            <div className="text-center slider-content">
              <p>{slides[current].footer}</p>
            </div>
          )}

          <div className="buttons d-flex align-items-center justify-content-center">
            {/* col-md-6 */}
            <div className="col-6">
              <Button
                variant="secondary"
                onClick={onSkip}
                className="btn btn-secondary"
              >
                Skip Tour
              </Button>
            </div>
            {/* col-md-6 */}
            <div className="col-6 d-flex justify-content-end">
              <Button
                variant="primary"
                onClick={handleNext}
                className="btn btn-primary"
              >
                {current < slides.length - 1 ? (
                  <>
                    Next <ChevronRightIcon className="button-icon" />
                  </>
                ) : (
                  "Get Started"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
    // </Modal>
  );
}
